import { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  MenuItem,
  FormControl,
  Divider,
  Paper,
  IconButton,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { apiCallVenueUpdate, apiCallVenueAdd } from "../../../Core/Apicall";
import { UploadImages } from "../../../Core/UploadImages";
import GoogleMapDialog from "../../Common/GoogleMap/GoogleMapDialog";
import uploadImage from "../../../assets/Images/Upload.png";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { getTimezonesForCountry } from "countries-and-timezones";
import { useSelector } from "react-redux";
import { RootState } from "../../../Redux/Store";
import { ObjectId } from "bson";

interface ProfileData {
  userId?: string;
  role?: number;
}

const VenueForm = () => {
  const location = useLocation();
  const venueData = location?.state?.venue;
  const userdata: any = useSelector((state: RootState) => state.global.token);
  const isProfileData = useSelector((state: any) => state?.global?.profileData) as ProfileData | null;

  const [formData, setFormData] = useState({
    venueId: "",
    venueName: "",
    address: "",
    zipCode: "",
    country: "",
    stateOrRegion: "",
    cityOrCounty: "",
    latitude: "",
    longitude: "",
    phoneNumber: "",
    phoneCountryCode: "",
    venueType: "",
    coverImage: { url: "", thumbnailUrl: "" },
    venueImages: [] as { url: string; thumbnailUrl: string }[],
    direction: "",
    timeZone: "",
  });

  const [errors, setErrors] = useState({
    venueName: "",
    phoneNumber: "",
    venueType: "",
    address: "",
    zipCode: "",
    direction: "",
    coverImage: "",
    venueImages: "",
    latitude: "",
    longitude: "",
  });

  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const validateForm = () => {
    let temp: any = {};
    let isValid = true;

    if (!formData.venueName.trim()) {
      temp.venueName = "Venue name is required.";
      isValid = false;
    }

    if (!formData.phoneNumber.trim()) {
      temp.phoneNumber = "Phone number is required.";
      isValid = false;
    } else if (formData.phoneNumber.length < 7) {
      temp.phoneNumber = "Enter a valid phone number.";
      isValid = false;
    }

    if (!formData.venueType) {
      temp.venueType = "Venue type is required.";
      isValid = false;
    }

    if (!formData.address.trim()) {
      temp.address = "Address is required.";
      isValid = false;
    }

    if (!formData.zipCode) {
      temp.zipCode = "Zip Code is required.";
      isValid = false;
    }

    if (!formData.direction) {
      temp.direction = "Direction is required.";
      isValid = false;
    }

    if (!formData.latitude) {
      temp.latitude = "Please pick a location on map.";
      isValid = false;
    }

    if (!formData.longitude) {
      temp.longitude = "Please pick a location on map.";
      isValid = false;
    }

    if (!formData.coverImage.url) {
      temp.coverImage = "Cover image is required.";
      isValid = false;
    }

    if (!formData.venueImages.length) {
      temp.venueImages = "At least 1 venue image is required.";
      isValid = false;
    }

    setErrors(temp);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors.");
      return;
    }

    try {
      const payload = {
        ...formData,
        timeZone: formData.timeZone,
        userId: userdata?.data?.userId,
      };
      if (venueData) {
        await apiCallVenueUpdate(payload);
        toast.success("Venue Update Successfully");
      } else {
        const addPayload = {
          ...formData,
          timeZone: formData.timeZone,
          userId: userdata?.data?.userId,
          _id: formData.venueId,
        };
        await apiCallVenueAdd(addPayload);
        toast.success("Venue Add Successfully");
      }

      setErrors({
        venueName: "",
        phoneNumber: "",
        venueType: "",
        address: "",
        zipCode: "",
        direction: "",
        coverImage: "",
        venueImages: "",
        latitude: "",
        longitude: "",
      });

      if (isProfileData?.role == 2) {
        navigate("/admin/venues");
      } else {
        navigate("/admin/venue-owner")
      }

    } catch (error) {
      toast.error("Failed to save venue. Please try again.");
    }
  };

  const emptyFormData = {
    venueId: "",
    venueName: "",
    address: "",
    zipCode: "",
    country: "",
    stateOrRegion: "",
    cityOrCounty: "",
    latitude: "",
    longitude: "",
    phoneNumber: "",
    phoneCountryCode: "",
    venueType: "",
    coverImage: { url: "", thumbnailUrl: "" },
    venueImages: [] as { url: string; thumbnailUrl: string }[],
    direction: "",
    timeZone: "",
  };

  useEffect(() => {
    if (location.pathname === "/admin/VenueUpdate" && venueData) {
      setFormData({
        ...emptyFormData,
        ...venueData,
        timeZone: venueData.timeZone,
        coverImage: venueData.coverImage || { url: "", thumbnailUrl: "" },
        venueId: venueData._id || "",
      });
    }

    if (location.pathname === "/admin/Add-Venue") {
      const id = new ObjectId();
      setFormData({
        ...emptyFormData,
        venueId: id.toHexString(),
      });
    }
  }, [venueData, location.pathname]);

  const locations = [
    { value: "N", label: "North" },
    { value: "NE", label: "North-East" },
    { value: "NW", label: "North-West" },
    { value: "E", label: "East" },
    { value: "S", label: "South" },
    { value: "SE", label: "South-East" },
    { value: "SW", label: "South-West" },
    { value: "W", label: "West" },
  ];

  const handlePhoneChange = (
    value: string,
    country: { dialCode: string; countryCode: string }
  ) => {
    const numberOnly = value.replace(country.dialCode, "").trim();
    setFormData({
      ...formData,
      phoneNumber: numberOnly,
      phoneCountryCode: `+${country.dialCode}`,
    });
  };

  const onSetTimeZone = (place: any) => {
    const countryCode =
      place?.address_components?.find((c: any) => c.types.includes("country"))
        ?.short_name || formData.country;
    const timezones = getTimezonesForCountry(countryCode);
    if (!timezones || timezones.length === 0) return "UTC";
    const cityName =
      place?.address_components?.find(
        (c: any) =>
          c.types.includes("locality") ||
          c.types.includes("administrative_area_level_2")
      )?.long_name || formData.cityOrCounty;
    const timeZone =
      timezones.find((tz) =>
        tz.name.toLowerCase().includes(cityName?.toLowerCase() || "")
      )?.name || timezones[0].name;

    setFormData((prev) => ({
      ...prev,
      timeZone: timeZone,
      address: place?.vicinity || prev.address,
      cityOrCounty:
        place?.address_components?.find(
          (c: any) =>
            c.types.includes("locality") ||
            c.types.includes("administrative_area_level_2")
        )?.long_name || prev.cityOrCounty,

      stateOrRegion:
        place?.address_components?.find((c: any) =>
          c.types.includes("administrative_area_level_1")
        )?.long_name || prev.stateOrRegion,
      country:
        place?.address_components?.find((c: any) => c.types.includes("country"))
          ?.long_name || prev.country,
      zipCode:
        place?.address_components?.find((c: any) =>
          c.types.includes("postal_code")
        )?.long_name || "",
    }));
  };

  return (
    <Box sx={{ p: 3 }} borderRadius={3}>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Personal Details */}
          <Grid size={12}>
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
              <Typography variant="h6" gutterBottom>
                Venue Details
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid size={4}>
                  <TextField
                    label="Venue Name"
                    name="venueName"
                    value={formData.venueName}
                    onChange={handleChange}
                    fullWidth
                  />
                  <Typography color="error" sx={{ fontSize: "12px", mt: 0.5 }}>
                    {errors.venueName}
                  </Typography>
                </Grid>
                <Grid size={4}>
                  <PhoneInput
                    country={"us"}
                    value={`${formData.phoneCountryCode.replace("+", "")}${formData.phoneNumber
                      }`}
                    onChange={handlePhoneChange}
                    inputStyle={{ width: "100%", height: "56px" }}
                  />
                  <Typography color="error" sx={{ fontSize: "12px", mt: 0.5 }}>
                    {errors.phoneNumber}
                  </Typography>
                </Grid>
                <Grid size={4}>
                  <TextField
                    select
                    label="Venue Type"
                    name="venueType"
                    value={formData.venueType}
                    onChange={handleChange}
                    fullWidth
                  >
                    <MenuItem value="Restaurant">Restaurant</MenuItem>
                    <MenuItem value="Hotel">Hotel</MenuItem>
                    <MenuItem value="Bar">Bar</MenuItem>
                    <MenuItem value="Pub">Pub</MenuItem>
                    <MenuItem value="Shop">Shop</MenuItem>
                  </TextField>
                  <Typography color="error" sx={{ fontSize: "12px", mt: 0.5 }}>
                    {errors.venueType}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Address */}
          <Grid size={12}>
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
              <Typography variant="h6" gutterBottom>
                Address
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid size={3}>
                  <TextField
                    label="Address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    fullWidth
                  />
                  <Typography color="error" sx={{ fontSize: "12px", mt: 0.5 }}>
                    {errors.address}
                  </Typography>
                </Grid>
                <Grid size={3}>
                  <TextField
                    label="Zip Code"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleChange}
                    fullWidth
                    type="number"
                  />
                  <Typography color="error" sx={{ fontSize: "12px", mt: 0.5 }}>
                    {errors.zipCode}
                  </Typography>
                </Grid>
                <Grid size={3}>
                  <TextField
                    label="City or County"
                    name="cityOrCounty"
                    value={formData.cityOrCounty}
                    onChange={handleChange}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid size={3}>
                  <TextField
                    label="State or Region"
                    name="stateOrRegion"
                    value={formData.stateOrRegion}
                    onChange={handleChange}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid size={3}>
                  <TextField
                    label="Country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid size={3}>
                  <TextField
                    // style={{ marginTop: "0px" }}
                    select
                    label="Direction"
                    name="direction"
                    value={formData.direction}
                    onChange={handleChange}
                    fullWidth
                  >
                    {locations.map((loc) => (
                      <MenuItem key={loc.value} value={loc.value}>
                        {loc.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Typography color="error" sx={{ fontSize: "12px", mt: 0.5 }}>
                    {errors.direction}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Box
                    sx={{
                      borderRadius: 3,
                      overflow: "hidden",
                      boxShadow: 3,
                    }}
                  >
                    <GoogleMapDialog
                      lat={Number(formData.latitude) || 22.308}
                      lng={Number(formData.longitude) || 70.815}
                      setLat={(newLat) =>
                        setFormData((prev) => ({
                          ...prev,
                          latitude: newLat.toString(),
                        }))
                      }
                      setLng={(newLng) =>
                        setFormData((prev) => ({
                          ...prev,
                          longitude: newLng.toString(),
                        }))
                      }
                      setAddress={(place) => {
                        onSetTimeZone(place);
                      }}
                      isReadOnly={false}
                    />
                  </Box>

                  <Box
                    display="flex"
                    gap={10}
                    alignItems="center"
                    mt={2}
                    px={1}
                  >
                    <Typography variant="body2">
                      <h5>Latitude: {formData.latitude || "--"}</h5>
                    </Typography>
                    <Typography variant="body2">
                      <h5>Longitude: {formData.longitude || "--"}</h5>
                    </Typography>
                  </Box>
                  <Typography color="error" sx={{ fontSize: "12px", mt: 0.5 }}>
                    {errors.latitude || errors.longitude}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid size={12}>
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
              <Typography variant="h6" gutterBottom>
                Upload Images:
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {/* Cover Image Upload */}
              <FormControl fullWidth>
                <Typography variant="subtitle2" sx={{ color: "black" }}>
                  Cover Image <span style={{ color: "red" }}>*</span>
                </Typography>

                <Box
                  component="label"
                  htmlFor="cover-upload"
                  mt={1}
                  border="2px solid #0000002A"
                  borderRadius={2}
                  p={8}
                  sx={{ cursor: "pointer", position: "relative" }}
                >
                  <input
                    type="file"
                    id="cover-upload"
                    hidden
                    accept="image/*"
                    onChange={async (
                      e: React.ChangeEvent<HTMLInputElement>
                    ) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const previewUrl = URL.createObjectURL(file);
                      setFormData((prev) => ({
                        ...prev,
                        coverImage: {
                          url: previewUrl,
                          thumbnailUrl: previewUrl,
                        },
                      }));

                      try {
                        const res = await UploadImages(
                          file,
                          "venue_cover",
                          formData.venueId
                        );
                        if (res?.location) {
                          setFormData((prev) => ({
                            ...prev,
                            coverImage: {
                              url: res.location,
                              thumbnailUrl: res.thumbnail,
                            },
                          }));
                        }
                      } catch (err) { }
                    }}
                  />

                  <Box
                    className="d-flex justify-content-center align-items-center"
                    position="absolute"
                    top="50%"
                    left="50%"
                    sx={{ transform: "translate(-50%, -50%)" }}
                  >
                    <Box position="relative" display="inline-block">
                      <img
                        height="60"
                        color="#3e3e3eff"
                        src={formData.coverImage.url || uploadImage}
                        alt="cover"
                        style={{ borderRadius: 6, objectFit: "cover" }}
                      />

                      {formData.coverImage.url && (
                        <Button
                          variant="outlined"
                          color="inherit"
                          size="small"
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              coverImage: { url: "", thumbnailUrl: "" },
                            }))
                          }
                          sx={{
                            position: "absolute",
                            top: "-10px",
                            right: "-10px",
                            minWidth: 24,
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            p: 0,
                            lineHeight: 1,
                            backgroundColor: "white",
                          }}
                        >
                          ✕
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>
                <Typography color="error" sx={{ fontSize: "12px", mt: 0.5 }}>
                  {errors.coverImage}
                </Typography>
              </FormControl>

              <Typography variant="subtitle2" sx={{ color: "black", mt: 2 }}>
                Venue Images Upload <span style={{ color: "red" }}>*</span>
              </Typography>

              <Grid size={12}>
                <Box
                  mt={2}
                  sx={{
                    display: "flex",
                    alignItems: "center", // vertically center
                    gap: 2,
                  }}
                >
                  {/* Upload Icon Button */}
                  <IconButton
                    component="label"
                    sx={{
                      p: 3,
                      border: "2px dashed #999",
                      borderRadius: 3,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 120,
                      height: 120,
                      cursor: "pointer",
                    }}
                  >
                    <img
                      src={uploadImage}
                      alt="Upload"
                      style={{ width: 40, height: 40, opacity: 0.7 }}
                    />
                    <Typography variant="caption" sx={{ mt: 1, color: "#666" }}>
                      Upload
                    </Typography>

                    <input
                      type="file"
                      hidden
                      multiple
                      accept="image/*"
                      onChange={async (
                        e: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        if (!e.target.files) return;
                        const files = Array.from(e.target.files);
                        const uploadedImages: {
                          url: string;
                          thumbnailUrl: string;
                        }[] = [];
                        for (const file of files) {
                          try {
                            const res = await UploadImages(
                              file,
                              "venue_cover",
                              formData.venueId
                            );
                            if (res?.location)
                              // uploadedImages.push(res.location);
                              uploadedImages.push({
                                url: res.location,
                                thumbnailUrl: res.thumbnail,
                              });
                          } catch (error) { }
                        }

                        setFormData((prev) => ({
                          ...prev,
                          venueImages: [
                            ...prev.venueImages, // keep old uploaded images
                            ...uploadedImages.map((img) => ({
                              url: img.url, // server URL
                              thumbnailUrl: img.thumbnailUrl, // keep thumbnail if needed
                            })),
                          ],
                        }));
                      }}
                    />
                  </IconButton>

                  {/* Image Preview (to the right side now) */}
                  {formData.venueImages.length > 0 && (
                    <Box sx={{ display: "flex", gap: 2 }}>
                      {formData.venueImages.map((img, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            position: "relative",
                            width: 90,
                            height: 90,
                          }}
                        >
                          {/* Image */}
                          <img
                            // src={img}
                            src={img.url}
                            alt={`venue-${idx}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              borderRadius: "50%",
                              border: "2px solid #000000ff",
                              overflow: "hidden",
                              objectFit: "cover",
                            }}
                          />

                          {/* Delete Button */}
                          <Button
                            size="small"
                            sx={{
                              position: "absolute",
                              top: 2, // move inside the circle
                              right: 2,
                              minWidth: 20,
                              width: 22,
                              height: 22,
                              borderRadius: "50%",
                              p: 0,
                              lineHeight: 1,
                              backgroundColor: "white",
                              boxShadow: 1,
                              "&:hover": {
                                backgroundColor: "#f5f5f5",
                              },
                            }}
                            onClick={() => {
                              const updated = [...formData.venueImages];
                              updated.splice(idx, 1);
                              setFormData((prev) => ({
                                ...prev,
                                venueImages: updated,
                              }));
                            }}
                          >
                            ✕
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
                <Typography color="error" sx={{ fontSize: "12px", mt: 0.5 }}>
                  {errors.venueImages}
                </Typography>
              </Grid>
            </Paper>
          </Grid>

          {/* Submit */}
          <Grid size={12}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                type="submit"
                variant="contained"
                sx={{ bgcolor: "black" }}
              >
                Submit
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default VenueForm;
